import {
    Carousel,
    FlexItem,
    Image,
    pxToRem,
    useStyles,
} from '@archipro-design/aria';
import React from 'react';
import type { Swiper as SwiperCore } from 'swiper';

import ImageSlide from './ImageSlide';
import VideoSlide from './VideoSlide';
import * as S from './ProductGallery.style';
import PhotoViewerModalMobile from './ProductViewerModal.mobile';
import { isLibraryVideo } from '@modules/product/util/is-library-video';
import SaveToDesignBoard from '~/modules/design-board/component/SaveToDesignBoard';
import useGalleryVariantSelect from '@modules/product/hook/use-gallery-variant-select';
import getGallerySlides from '~/modules/product/util/get-gallery-slides';
import type {
    ProductDiagramFragmentFragment,
    ProductImageFragmentFragment,
} from 'generated/graphql';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import { PreventCloseGesture } from '~/modules/prevent-close-gesture/PreventCloseGesture';
import { useFavouriteVariant } from '../../hook/use-favourite-variant';
import { useLatest } from 'ahooks';
import BannerAwards from '@modules/page-builder/component/detail/banner-awards/BannerAwards';
import { hasFeature, useAppData } from '@modules/root';

interface ProductGalleryProps {
    data: ProductPageLoaderData['product'];
    firstSlide: string;
    title?: string;
}

const getSlideOffset = (slidesLength: number) => {
    const slidesOffsetAfter = 9;
    const spaceBetween = slidesLength > 1 ? 6 : 0;
    const slidesPerView = slidesLength > 1 ? 1.1 : 1;
    const slidesOffsetBefore = slidesLength > 1 ? slidesOffsetAfter : 0;
    return {
        spaceBetween,
        slidesPerView,
        slidesOffsetBefore,
        slidesOffsetAfter,
    };
};

const ProductGalleryMobile = ({
    data,
    firstSlide,
    title,
}: ProductGalleryProps) => {
    const {
        LibraryImages,
        LibraryVideos,
        DiagramImages,
        Title,
        Professional,
        ID,
        Awards,
    } = data;

    const galleryRef = React.useRef<SwiperCore>();
    const thumbsRef = React.useRef<SwiperCore>();

    const stack = getGallerySlides({
        LibraryImages,
        LibraryVideos,
        DiagramImages,
    });

    const { slideIndex } = useGalleryVariantSelect(stack);
    const slideIndexRef = useLatest(slideIndex);
    const [modalIndex, setModalIndex] = React.useState<number>(slideIndex);
    const [modalOpen, setModalOpen] = React.useState<boolean>(false);
    const [swiperInitialised, setSwiperInitialised] = React.useState<boolean>(
        !!galleryRef.current
    );

    const { user } = useAppData();
    const enableAwardBadges = hasFeature(user.FeatureFlags, 'award_badges');

    const { css } = useStyles({
        swiperInitialised,
    });

    const slides = React.useMemo(() => {
        return stack.map((slide, idx) => {
            const isVideo = isLibraryVideo(slide);
            const imageThumbnailSrc = !isVideo
                ? slide.__typename === 'DiagramImage'
                    ? slide.LibraryImageFile?.ThumbnailBM180
                    : slide.LibraryImageFile?.ThumbnailBM180
                : slide.ThumbnailOfficial;
            const isMultiplied =
                slide.__typename === 'LibraryImage'
                    ? slide.IsMultiplied
                    : false;

            return {
                key: `${slide.ID}-ImageSlide`,
                id: slide.ID,
                content: !isVideo ? (
                    <ImageSlideWithFavourite
                        firstSlide={firstSlide}
                        index={idx}
                        setModalOpen={setModalOpen}
                        slide={slide}
                        title={title}
                        productID={ID}
                    />
                ) : (
                    <div className={css(S.VideoSlideWrapper)}>
                        <VideoSlide libraryVideo={slide} />
                    </div>
                ),
                thumbnail: (
                    <Image
                        alt={
                            (slide.__typename !== 'LibraryVideo' &&
                                slide.AltText) ||
                            `${title} product gallery detail image`
                        }
                        src={imageThumbnailSrc ?? ''}
                        mixBlendMode={isMultiplied ? 'multiply' : 'normal'}
                        width={90}
                        height={90}
                        layout={'responsive'}
                        loading={idx <= 1 ? 'eager' : 'lazy'}
                    />
                ),
            };
        });
    }, [ID, stack, firstSlide, title, css]);

    React.useEffect(() => {
        const gallery = galleryRef.current;
        if (!gallery) return;

        gallery.slideTo(slideIndex);
    }, [slideIndex]);

    React.useEffect(() => {
        const gallery = galleryRef.current;
        if (!gallery) return;

        gallery.slideTo(slideIndexRef.current ?? 0, 0);
    }, [data.ID, slideIndexRef]);

    const handleThumbsTransition = (
        activeIndex: number,
        slideCount: number
    ) => {
        const thumbsSwiper = thumbsRef.current;
        if (!thumbsSwiper) return;

        const THUMB_SIZE = 96.33;
        const THUMB_BOUND_EDGES = 20;
        const LAST_THUMB_GROUP_EDGE = 17;
        const MAX_SLIDE_BOUNDS = slideCount - 4;

        if (activeIndex < 4) {
            thumbsSwiper.translateTo(18, 300);
        } else if (activeIndex > slideCount - 5) {
            thumbsSwiper.translateTo(
                -MAX_SLIDE_BOUNDS * THUMB_SIZE + LAST_THUMB_GROUP_EDGE,
                300
            );
        } else {
            thumbsSwiper.translateTo(
                -activeIndex * THUMB_SIZE + THUMB_BOUND_EDGES,
                300
            );
        }
    };

    galleryRef.current?.on('slideChange', ({ activeIndex, slides }) => {
        setModalIndex(activeIndex);
        handleThumbsTransition(activeIndex, slides.length);
    });

    const {
        slidesOffsetAfter,
        spaceBetween,
        slidesPerView,
        slidesOffsetBefore,
    } = getSlideOffset(slides.length);

    return (
        <PreventCloseGesture>
            <div className={css(S.GalleryWrapper)}>
                <Carousel
                    items={slides}
                    slidesPerView={slidesPerView}
                    spaceBetween={spaceBetween}
                    slidesOffsetBefore={slidesOffsetBefore}
                    slidesOffsetAfter={slidesOffsetAfter}
                    getSwiperApi={(swiper) => {
                        galleryRef.current = swiper as SwiperCore;
                        setSwiperInitialised(true);
                    }}
                    freeMode={true}
                    thumbs={{ swiper: thumbsRef?.current }}
                    hidePaddles
                    className={css(S.Carousel)}
                    variables={{
                        containerWidth: pxToRem(414),
                        containerHeight: pxToRem(378),
                        overlayInitialOpacity: 1,
                    }}
                />

                {enableAwardBadges && Awards && Awards.length > 0 && (
                    <BannerAwards
                        awards={Awards}
                        heading={'Award winning product'}
                    />
                )}

                {modalOpen && (
                    <PhotoViewerModalMobile
                        stack={stack}
                        productTitle={Title}
                        professional={Professional}
                        selectedSlide={modalIndex}
                        open={modalOpen}
                        toggle={() => setModalOpen((curr) => !curr)}
                    />
                )}
            </div>
        </PreventCloseGesture>
    );
};

const ImageSlideWithFavourite = ({
    slide,
    title,
    // setModalOpen,
    firstSlide,
    index,
    productID,
}: {
    slide: ProductImageFragmentFragment | ProductDiagramFragmentFragment;
    title: string | undefined;
    setModalOpen: (open: boolean) => void;
    firstSlide: string;
    index: number;
    productID: number;
}) => {
    const { css } = useStyles();
    const favouriteItem = useFavouriteVariant(productID);

    return (
        <>
            <FlexItem push key="favourite-button">
                <SaveToDesignBoard
                    {...favouriteItem}
                    heartButton={{
                        className: css(S.FavouriteButton),
                        size: 24,
                        color: 'primary',
                        variables: {
                            buttonPadding: pxToRem(0),
                            buttonRadius: '50%',
                            primaryButtonBackgroundColorNormal:
                                'rgba(250, 250, 250, 0.7)',
                        },
                    }}
                />
            </FlexItem>
            <ImageSlide
                libraryImage={
                    index === 0
                        ? firstSlide
                        : slide.LibraryImageFile?.DetailPageSquare.Mobile ?? ''
                }
                isMultiplied={slide.IsMultiplied}
                // onClick={() => setModalOpen(true)} // Kieran disabled this to try improve conversion rates. We can try re-enabling this after October 2024 as long as we first add save buttons to the modal photos
                height={378}
                width={378}
                loadingValue={index === 0 ? 'eager' : 'lazy'}
                sizes="100vw"
                alt={slide.AltText || title}
            />
        </>
    );
};

export default ProductGalleryMobile;
